import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { calendar, moduleColors } from '../data/studyData';
import { BookOpen, Clock, GraduationCap, Target, Calendar as CalendarIcon, Video } from 'lucide-react';

export const ProgressStats = ({ completedTasks, selectedDate }) => {
  const stats = useMemo(() => {
    const totalStudyHours = calendar.reduce((sum, day) => sum + day.study_hours, 0);
    const totalTasks = calendar.reduce((sum, day) => sum + day.tasks.length, 0);
    const completedCount = Object.values(completedTasks).flat().length;
    
    const mst124Hours = calendar
      .filter(d => d.module === 'MST124' || d.module === 'Both')
      .reduce((sum, d) => sum + d.study_hours, 0);
    
    const sm123Hours = calendar
      .filter(d => d.module === 'SM123' || d.module === 'Both')
      .reduce((sum, d) => sum + d.study_hours, 0);
    
    const totalTutorials = calendar.reduce((sum, day) => sum + day.tutorials.length, 0);
    
    const studyDays = calendar.filter(d => d.study_hours > 0).length;
    const restDays = calendar.filter(d => d.module === 'Rest').length;
    
    // Calculate progress up to selected date
    const selectedDateStr = selectedDate.toISOString().split('T')[0];
    const daysUpToSelected = calendar.filter(d => d.date <= selectedDateStr);
    const hoursCompleted = daysUpToSelected.reduce((sum, d) => sum + d.study_hours, 0);

    // Exam countdowns
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const mst124Exam = new Date('2026-06-03');
    const sm123Exam = new Date('2026-06-10');
    const mst124Days = Math.ceil((mst124Exam - today) / (1000 * 60 * 60 * 24));
    const sm123Days = Math.ceil((sm123Exam - today) / (1000 * 60 * 60 * 24));
    
    return {
      totalStudyHours,
      totalTasks,
      completedCount,
      mst124Hours,
      sm123Hours,
      totalTutorials,
      studyDays,
      restDays,
      hoursCompleted,
      progressPercent: Math.round((hoursCompleted / totalStudyHours) * 100),
      mst124Days,
      sm123Days
    };
  }, [completedTasks, selectedDate]);

  const StatCard = ({ icon: Icon, label, value, subValue, color }) => (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/50">
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-heading font-bold">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
        {subValue && (
          <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>
        )}
      </div>
    </div>
  );

  return (
    <Card 
      className="stats-card rounded-2xl border-2 shadow-sm"
      data-testid="progress-stats"
    >
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Target className="w-5 h-5 text-primary" />
          Progress Overview
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Overall Progress</span>
            <span className="text-sm font-bold text-primary">{stats.progressPercent}%</span>
          </div>
          <Progress 
            value={stats.progressPercent} 
            className="h-3 rounded-full"
            data-testid="overall-progress-bar"
          />
          <p className="text-xs text-muted-foreground">
            {stats.hoursCompleted} of {stats.totalStudyHours} hours planned
          </p>
        </div>

        {/* Exam Countdown */}
        <div className="space-y-3" data-testid="exam-countdown">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Exams
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl border-2 border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
              <div className="flex items-center gap-1.5 mb-1">
                <GraduationCap className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                <p className="text-xs font-semibold text-red-700 dark:text-red-300">MST124</p>
              </div>
              <p className="text-lg font-heading font-bold text-red-800 dark:text-red-200">
                {stats.mst124Days > 0 ? `${stats.mst124Days} days` : 'Today!'}
              </p>
              <p className="text-xs text-red-600 dark:text-red-400">Wed 3 June</p>
            </div>
            <div className="p-3 rounded-xl border-2 border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
              <div className="flex items-center gap-1.5 mb-1">
                <GraduationCap className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                <p className="text-xs font-semibold text-red-700 dark:text-red-300">SM123</p>
              </div>
              <p className="text-lg font-heading font-bold text-red-800 dark:text-red-200">
                {stats.sm123Days > 0 ? `${stats.sm123Days} days` : 'Today!'}
              </p>
              <p className="text-xs text-red-600 dark:text-red-400">Wed 10 June</p>
            </div>
          </div>
        </div>

        {/* Module Breakdown */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Module Hours
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div className={`p-3 rounded-xl ${moduleColors.MST124.bg} ${moduleColors.MST124.border} border`}>
              <p className={`text-xl font-heading font-bold ${moduleColors.MST124.text}`}>
                {stats.mst124Hours}h
              </p>
              <p className={`text-xs font-medium ${moduleColors.MST124.text} opacity-80`}>
                MST124 Maths
              </p>
            </div>
            <div className={`p-3 rounded-xl ${moduleColors.SM123.bg} ${moduleColors.SM123.border} border`}>
              <p className={`text-xl font-heading font-bold ${moduleColors.SM123.text}`}>
                {stats.sm123Hours}h
              </p>
              <p className={`text-xs font-medium ${moduleColors.SM123.text} opacity-80`}>
                SM123 Science
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 gap-3">
          <StatCard 
            icon={Clock}
            label="Total Study Hours"
            value={stats.totalStudyHours}
            subValue={`${stats.studyDays} study days`}
            color="bg-sky-600"
          />
          <StatCard 
            icon={BookOpen}
            label="Total Tasks"
            value={stats.totalTasks}
            subValue={`${stats.completedCount} completed`}
            color="bg-emerald-600"
          />
          <StatCard 
            icon={Video}
            label="Tutorials Scheduled"
            value={stats.totalTutorials}
            color="bg-amber-600"
          />
          <StatCard 
            icon={CalendarIcon}
            label="Rest Days"
            value={stats.restDays}
            subValue="2 per week"
            color="bg-slate-500"
          />
        </div>

        {/* Task Completion */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Tasks Completed</span>
            <span className="text-sm font-bold text-accent">
              {stats.completedCount}/{stats.totalTasks}
            </span>
          </div>
          <Progress 
            value={(stats.completedCount / stats.totalTasks) * 100} 
            className="h-3 rounded-full bg-accent/20 [&>div]:bg-accent"
            data-testid="tasks-progress-bar"
          />
        </div>
      </CardContent>
    </Card>
  );
};
